import { useEffect, useState } from "react";
import { useNavigate, useSearchParams, useLocation } from "react-router-dom";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const Field = ({ label, ...p }) => (
  <label className="block">
    <span className="mb-2 block text-xs uppercase tracking-widest text-neutral-500">{label}</span>
    <input {...p} className="h-12 w-full rounded-xl border border-white/10 bg-[#0A0A0B] px-4 text-sm outline-none transition-colors focus:border-[#D4AF37]/70" />
  </label>
);

export default function Login() {
  const [params] = useSearchParams();
  const [mode, setMode] = useState(params.get("mode") === "register" ? "register" : "login");
  const [form, setForm] = useState({ name: "", email: "", password: "" });
  const [busy, setBusy] = useState(false);
  const { user, setUser } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const dest = location.state?.from || "/studio";

  useEffect(() => { if (user) navigate(dest, { replace: true }); }, [user, navigate, dest]);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const body = mode === "register" ? form : { email: form.email, password: form.password };
      const { data } = await api.post(`/auth/${mode}`, body);
      setUser(data);
      toast.success(mode === "register" ? "Willkommen bei vyne!" : "Schön, dass du da bist!");
    } catch (err) {
      toast.error(errMsg(err));
    } finally { setBusy(false); }
  };

  const google = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/studio";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  return (
    <div className="mx-auto grid min-h-[80vh] max-w-6xl grid-cols-1 items-center gap-12 px-4 py-16 md:grid-cols-2" data-testid="login-page">
      <div className="fade-up hidden md:block">
        <img src="/logo.png" alt="vyne" className="h-40 w-40 rounded-[22%] ring-1 ring-white/10" />
        <h1 className="mt-10 font-serif text-5xl leading-tight">Willkommen in<br />deinem <em className="text-[#D4AF37]">Style-Studio.</em></h1>
        <p className="mt-4 max-w-sm text-neutral-400">Avatar erstellen, Kleiderschrank digitalisieren und Outfits aus deinen Lieblingsmarken zusammenstellen.</p>
      </div>
      <div className="surface fade-up p-8 sm:p-10">
        <div className="mb-8 flex rounded-full border border-white/10 p-1">
          {[["login", "Anmelden"], ["register", "Registrieren"]].map(([m, l]) => (
            <button key={m} onClick={() => setMode(m)} className={`flex-1 rounded-full py-2.5 text-sm transition-colors ${mode === m ? "bg-[#FAF7F0] font-semibold text-black" : "text-neutral-400"}`} data-testid={`auth-tab-${m}`}>{l}</button>
          ))}
        </div>
        <form onSubmit={submit} className="space-y-5" data-testid="auth-form">
          {mode === "register" && <Field label="Name" value={form.name} onChange={set("name")} required data-testid="auth-name-input" />}
          <Field label="E-Mail" type="email" value={form.email} onChange={set("email")} required data-testid="auth-email-input" />
          <Field label="Passwort" type="password" minLength={6} value={form.password} onChange={set("password")} required data-testid="auth-password-input" />
          <button type="submit" disabled={busy} className="btn-cream w-full" data-testid="auth-submit-button">
            {busy ? "Bitte warten…" : mode === "register" ? "Konto erstellen" : "Anmelden"}
          </button>
        </form>
        <div className="my-6 flex items-center gap-4 text-xs text-neutral-600"><span className="h-px flex-1 bg-white/10" />oder<span className="h-px flex-1 bg-white/10" /></div>
        <button onClick={google} className="btn-ghost w-full" data-testid="google-login-button">
          <svg className="h-4 w-4" viewBox="0 0 48 48"><path fill="#FFC107" d="M43.6 20.5H42V20H24v8h11.3C33.7 32.7 29.2 36 24 36c-6.6 0-12-5.4-12-12s5.4-12 12-12c3 0 5.8 1.1 7.9 3l5.7-5.7C34 6.1 29.3 4 24 4 12.9 4 4 12.9 4 24s8.9 20 20 20 20-8.9 20-20c0-1.3-.1-2.4-.4-3.5z" /><path fill="#FF3D00" d="m6.3 14.7 6.6 4.8C14.7 15.1 19 12 24 12c3 0 5.8 1.1 7.9 3l5.7-5.7C34 6.1 29.3 4 24 4 16.3 4 9.7 8.3 6.3 14.7z" /><path fill="#4CAF50" d="M24 44c5.2 0 9.9-2 13.4-5.2l-6.2-5.2C29.2 35.1 26.7 36 24 36c-5.2 0-9.6-3.3-11.3-8l-6.5 5C9.5 39.6 16.2 44 24 44z" /><path fill="#1976D2" d="M43.6 20.5H42V20H24v8h11.3c-.8 2.2-2.2 4.2-4.1 5.6l6.2 5.2C37 39.2 44 34 44 24c0-1.3-.1-2.4-.4-3.5z" /></svg>
          Mit Google fortfahren
        </button>
      </div>
    </div>
  );
}
